import dayjs from "dayjs";
import { useMemo } from "react";
import { useFetchCurrentSubscriptionQuery } from "./queries/clinic/useFetchCurrentSubscriptionQuery";
import {
  CurrentSubscriptionStatus,
  SubscriptionPlanCode,
} from "@client/resources/CurrentSubscriptionResource";

export const useClinicSubscription = () => {
  const { data: currentSubscriptionResponse, isLoading } =
    useFetchCurrentSubscriptionQuery();

  const subscriptionFromApi = useMemo(
    () => currentSubscriptionResponse?.data ?? null,
    [currentSubscriptionResponse],
  );

  const subscription = useMemo(() => {
    if (!subscriptionFromApi) {
      return null;
    }

    return {
      uuid: subscriptionFromApi.uuid,
      plan: subscriptionFromApi.plan,
      name: subscriptionFromApi.plan?.name ?? "",
      code: subscriptionFromApi.plan?.code,
      createdAt: subscriptionFromApi.createdAt,
      startsAt: subscriptionFromApi.startsAt,
      endsAt: subscriptionFromApi.endsAt,
      currentUsagePeriod: subscriptionFromApi.currentUsagePeriod,
    };
  }, [subscriptionFromApi]);

  const daysRemaining = calculateDaysRemaining(subscription?.endsAt ?? null);

  return {
    subscription,
    hasSubscription: currentSubscriptionResponse?.data !== null,
    hasValidStatus:
      currentSubscriptionResponse?.data?.status &&
      ALLOWED_CLINIC_STATUSES.includes(
        currentSubscriptionResponse?.data?.status as CurrentSubscriptionStatus,
      ),
    daysRemaining,
    startsAt: currentSubscriptionResponse?.data?.startsAt ?? null,
    endsAt: currentSubscriptionResponse?.data?.endsAt ?? null,
    isTrial: subscription?.code === SubscriptionPlanCode.TRIAL,
    isUnlimitedGrowth:
      subscription?.code === SubscriptionPlanCode.UNLIMITED_GROWTH,
    billingCycle: currentSubscriptionResponse?.data?.billingCycle ?? null,
    status: currentSubscriptionResponse?.data?.status ?? null,
    items: currentSubscriptionResponse?.data?.items ?? [],
    nextBillingAt: currentSubscriptionResponse?.data?.nextBillingAt ?? null,
    currentUsagePeriod: currentSubscriptionResponse?.data?.currentUsagePeriod,
    currentPeriod: currentSubscriptionResponse?.data?.currentPeriod ?? null,
    plan: currentSubscriptionResponse?.data?.plan,
    cancelAtPeriodEnd:
      currentSubscriptionResponse?.data?.cancelAtPeriodEnd ?? false,
    isLoading,
  };
};

const calculateDaysRemaining = (finishedAt: string | null): number | null => {
  if (!finishedAt) return null;

  const endDay = dayjs.utc(finishedAt).local().startOf("day");
  const todayStart = dayjs().startOf("day");
  return endDay.diff(todayStart, "days");
};

const ALLOWED_CLINIC_STATUSES = [
  CurrentSubscriptionStatus.Active,
  CurrentSubscriptionStatus.Trialing,
  CurrentSubscriptionStatus.PastDue,
];
